import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, Divider, Grid, Paper, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import CheckBoxInput from '../../../../../Components/CheckBoxInput';
import { FEATURE_DESCRIPTION } from '../../../constant';
import InfoButton from '../../../../../Components/InfoButton';
import PageHeading from '../../../../../Components/PageHeading';
import useCommonStyles from '../../../../../commonStyles';
import { UnitsContext } from '../../../../../Contexts/UnitsContext';
import { UserContext } from '../../../../../Contexts/UserContext';
import { ACCESS_CONTROL_CONSTANTS } from '../../../../../core/constants';
import {
	cancelRssVideoJob,
	createRssVideoFeed,
	createRssVideoJob,
	deleteRssVideoFeed,
	deleteRssVideoJob,
	getRssVideoFeed,
	getRssVideoJob,
	getAllRssVideoJobs,
	hardResetRssVideoJob,
	pollRssVideoFeed,
	retryRssVideoJob,
	updateRssVideoJobMetadata,
	updateRssVideoFeed
} from '../../../../../api/rssVideoGeneration';
import DeleteFeedDialog from './DeleteFeedDialog';
import FeedConfigurationCard from './FeedConfigurationCard';
import JobDetailsDialog from './JobDetailsDialog';
import JobsTable from './JobsTable';
import ManualJobCard from './ManualJobCard';
import {
	DEFAULT_COMPOSITION_ID,
	INITIAL_FEED_FORM,
	createInitialJobForm,
	PAGE_LEVEL_CONTENT_VIDEOS_KEY,
	RSS_VIDEO_GENERATION_CONFIG_KEY,
	SECTION_PAGE_LEVEL_CONTENT_VIDEOS_DISABLE_KEY
} from './constants';
import { formatDateTime, getErrorMessage, hasFeedConfigChanges, isCancelBlocked, isTerminalStage } from './helpers';

const PageLevelContent = ({ featureDescription }) => {
	const commonStyleClasses = useCommonStyles();
	const { siteId } = useParams();
	const jobDetailsRequest = useRef(0);
	const { siteRawConfig, curSelectedUnit, units, updateSiteRawConfig } = useContext(UnitsContext);
	const { user } = useContext(UserContext);
	const [feed, setFeed] = useState(null);
	const [jobs, setJobs] = useState([]);
	const [selectedJob, setSelectedJob] = useState(null);
	const [selectedJobDetails, setSelectedJobDetails] = useState(null);
	const [jobDetailsError, setJobDetailsError] = useState('');
	const [jobMetadataSaving, setJobMetadataSaving] = useState(false);
	const [jobHardResetting, setJobHardResetting] = useState(false);
	const [jobForm, setJobForm] = useState(createInitialJobForm);
	const [feedForm, setFeedForm] = useState(INITIAL_FEED_FORM);
	const [deleteFeedDialogOpen, setDeleteFeedDialogOpen] = useState(false);
	const [loading, setLoading] = useState({
		feed: true,
		jobs: true,
		jobDetails: false,
		saveFeed: false,
		deleteFeed: false,
		pollFeed: false,
		createJob: false,
		jobActionId: ''
	});

	if (!siteId) {
		throw new Error('Site ID is missing');
	}

	const isExperimentalUser = user?.controlledAccess?.includes(
		ACCESS_CONTROL_CONSTANTS.ACCESS_CONTROL_STRINGS.EXPERIMENTAL_FEATURES
	);
	const storedFeedConfig = siteRawConfig?.[RSS_VIDEO_GENERATION_CONFIG_KEY];
	// An empty object marks a cleared feed, since the site doc update merges shallowly server-side.
	const savedFeedConfig = storedFeedConfig && Object.keys(storedFeedConfig).length ? storedFeedConfig : null;

	const hasExistingFeed = Boolean(feed?.config);
	const isSiteEnabled = siteRawConfig?.pageLevelContentVideos?.enabled === true;
	const isSectionDisabled = units?.[curSelectedUnit]?.[SECTION_PAGE_LEVEL_CONTENT_VIDEOS_DISABLE_KEY] === true;

	const updateSiteEnabled = (enabled) => {
		updateSiteRawConfig((prev) => ({
			...prev,
			pageLevelContentVideos: { ...prev.pageLevelContentVideos, enabled }
		}));
	};

	const confirmGenerationWithoutDisplay = (action) => {
		if (!isSectionDisabled) {
			return true;
		}

		return window.confirm(
			`Page Level Content Videos is disabled for the active video section. ${action} will generate videos, but they will not display for this section until Disable for this section is unchecked in Features Data > Page Level Content Override. Continue?`
		);
	};

	const feedSummary = useMemo(() => {
		if (!feed?.config) {
			return [];
		}

		return [
			{ label: 'Last Polled', value: formatDateTime(feed.state?.lastPolledAt) },
			{ label: 'Last Published Date', value: formatDateTime(feed.state?.lastPubDate) },
			{ label: 'Daily Count', value: feed.dailyCount },
			{ label: 'Status', value: feed.config?.active ? 'Active' : 'Inactive' }
		];
	}, [feed]);

	const buildFeedConfig = (config = {}) => ({
		feedUrl: config.feedUrl || '',
		dailyLimit: config.dailyLimit ?? INITIAL_FEED_FORM.dailyLimit,
		pollIntervalMinutes:
			config.pollIntervalMinutes ??
			(config.pollIntervalMs ? config.pollIntervalMs / 60000 : INITIAL_FEED_FORM.pollIntervalMinutes),
		compositionId: config.compositionId || DEFAULT_COMPOSITION_ID,
		language: config.language || INITIAL_FEED_FORM.language,
		active: Boolean(config.active)
	});

	const needsFeedSync = !loading.feed && feed !== false &&
		hasFeedConfigChanges(feed?.config, savedFeedConfig);

	const handleSyncFeed = () => {
		if (feed?.config) {
			persistFeedConfig(feed.config);
			setFeedForm(buildFeedConfig(feed.config));
		} else {
			clearFeedConfig();
			setFeedForm(INITIAL_FEED_FORM);
		}
		toast.success('Site config updated with the live feed settings. Update the site to save this change.', {
			position: toast.POSITION.BOTTOM_RIGHT
		});
	};

	const updateJobForm = (key, value) => {
		setJobForm((prev) => ({ ...prev, [key]: value }));
	};

	const updateFeedForm = (key, value) => {
		setFeedForm((prev) => {
			const nextFeedConfig = { ...prev, [key]: value };
			return nextFeedConfig;
		});
	};

	const persistFeedConfig = (nextFeedConfig) => {
		updateSiteRawConfig((prev) => {
			const feedConfig = buildFeedConfig(nextFeedConfig);

			return {
				...prev,
				[RSS_VIDEO_GENERATION_CONFIG_KEY]: feedConfig
			};
		});
	};

	const clearFeedConfig = () => {
		updateSiteRawConfig((prev) => ({
			...prev,
			[RSS_VIDEO_GENERATION_CONFIG_KEY]: {}
		}));
	};

	const loadJobs = async ({ isCancelled } = {}) => {
		setLoading((prev) => ({ ...prev, jobs: true }));

		try {
			const { data } = await getAllRssVideoJobs(siteId);
			if (isCancelled?.()) {
				return;
			}
			setJobs(data?.data?.jobs || []);
		} catch (error) {
			if (isCancelled?.()) {
				return;
			}
			toast.error(getErrorMessage(error), { position: toast.POSITION.BOTTOM_RIGHT });
		} finally {
			if (!isCancelled?.()) {
				setLoading((prev) => ({ ...prev, jobs: false }));
			}
		}
	};

	const loadFeed = async ({ isCancelled, preferServerConfig = false } = {}) => {
		setLoading((prev) => ({ ...prev, feed: true }));

		try {
			const { data } = await getRssVideoFeed(siteId);
			if (isCancelled?.()) {
				return;
			}
			const nextFeed = data?.data?.feed || null;
			setFeed(nextFeed);
			if (nextFeed?.config) {
				const nextFeedConfig = buildFeedConfig(nextFeed.config);
				const configForForm = preferServerConfig
					? nextFeedConfig
					: buildFeedConfig(savedFeedConfig || nextFeedConfig);

				if (preferServerConfig) {
					persistFeedConfig(nextFeedConfig);
				}

				setFeedForm(configForForm);
			} else {
				setFeedForm(savedFeedConfig ? buildFeedConfig(savedFeedConfig) : INITIAL_FEED_FORM);
			}
		} catch (error) {
			if (isCancelled?.()) {
				return;
			}
			if (error?.response?.status === 404) {
				setFeed(null);
				setFeedForm(preferServerConfig ? INITIAL_FEED_FORM : savedFeedConfig || INITIAL_FEED_FORM);
				return;
			}

			setFeed(false);
			toast.error(getErrorMessage(error), { position: toast.POSITION.BOTTOM_RIGHT });
		} finally {
			if (!isCancelled?.()) {
				setLoading((prev) => ({ ...prev, feed: false }));
			}
		}
	};

	// to prevent stale data from previous page when switching between pages
	useEffect(() => {
		let cancelled = false;
		const isCancelled = () => cancelled;

		loadFeed({ isCancelled });
		loadJobs({ isCancelled });

		return () => {
			cancelled = true;
			jobDetailsRequest.current += 1;
		};
	}, [siteId]);

	useEffect(() => {
		if (savedFeedConfig) {
			setFeedForm(buildFeedConfig(savedFeedConfig));
			return;
		}

		if (!feed?.config) {
			setFeedForm(INITIAL_FEED_FORM);
		}
	}, [feed?.config, savedFeedConfig]);

	const handleSaveFeed = async () => {
		const isEnablingFeed = feedForm.active && (!hasExistingFeed || !feed?.config?.active);

		if (isEnablingFeed && !confirmGenerationWithoutDisplay('Enabling this feed')) {
			return;
		}

		setLoading((prev) => ({ ...prev, saveFeed: true }));

		const payload = {
			siteId,
			feedUrl: feedForm.feedUrl,
			dailyLimit: feedForm.dailyLimit,
			pollIntervalMinutes: feedForm.pollIntervalMinutes,
			compositionId: feedForm.compositionId,
			language: feedForm.language,
			active: feedForm.active
		};

		try {
			if (hasExistingFeed) {
				await updateRssVideoFeed(siteId, {
					feedUrl: payload.feedUrl,
					dailyLimit: payload.dailyLimit,
					pollIntervalMinutes: payload.pollIntervalMinutes,
					compositionId: payload.compositionId,
					language: payload.language,
					active: payload.active
				});
				toast.success('Feed updated successfully', { position: toast.POSITION.BOTTOM_RIGHT });
			} else {
				await createRssVideoFeed(payload);
				toast.success('Feed created successfully', { position: toast.POSITION.BOTTOM_RIGHT });
			}

			// Refresh from the API so local config matches any server-side normalization.
			await loadFeed({ preferServerConfig: true });
			toast.info('Update the site config to persist the feed settings.', {
				position: toast.POSITION.BOTTOM_RIGHT,
				autoClose: 8000
			});
		} catch (error) {
			toast.error(getErrorMessage(error), { position: toast.POSITION.BOTTOM_RIGHT });
		} finally {
			setLoading((prev) => ({ ...prev, saveFeed: false }));
		}
	};

	const handleDeleteFeed = async () => {
		setLoading((prev) => ({ ...prev, deleteFeed: true }));

		try {
			await deleteRssVideoFeed(siteId);
			setFeed(null);
			setFeedForm(INITIAL_FEED_FORM);
			clearFeedConfig();
			setDeleteFeedDialogOpen(false);
			toast.success('Feed deleted successfully', { position: toast.POSITION.BOTTOM_RIGHT });
			toast.info('Update the site config to persist the removed feed settings.', {
				position: toast.POSITION.BOTTOM_RIGHT,
				autoClose: 8000
			});
			await loadJobs();
		} catch (error) {
			toast.error(getErrorMessage(error), { position: toast.POSITION.BOTTOM_RIGHT });
		} finally {
			setLoading((prev) => ({ ...prev, deleteFeed: false }));
		}
	};

	const handlePollFeed = async () => {
		if (!confirmGenerationWithoutDisplay('Polling this feed')) {
			return;
		}

		setLoading((prev) => ({ ...prev, pollFeed: true }));

		try {
			await pollRssVideoFeed(siteId);
			toast.success('Feed poll triggered successfully', { position: toast.POSITION.BOTTOM_RIGHT });
			await Promise.all([loadFeed(), loadJobs()]);
		} catch (error) {
			toast.error(getErrorMessage(error), { position: toast.POSITION.BOTTOM_RIGHT });
		} finally {
			setLoading((prev) => ({ ...prev, pollFeed: false }));
		}
	};

	const handleCreateJob = async () => {
		if (!confirmGenerationWithoutDisplay('Creating this job')) {
			return;
		}

		setLoading((prev) => ({ ...prev, createJob: true }));

		try {
			await createRssVideoJob({
				siteId,
				pageURL: jobForm.pageURL,
				language: jobForm.language,
				compositionId: jobForm.compositionId,
				debug: jobForm.debug,
				pubDate: new Date().toISOString()
			});

			toast.success('Job created successfully', { position: toast.POSITION.BOTTOM_RIGHT });
			setJobForm(createInitialJobForm());
			await loadJobs();
		} catch (error) {
			toast.error(getErrorMessage(error), { position: toast.POSITION.BOTTOM_RIGHT });
		} finally {
			setLoading((prev) => ({ ...prev, createJob: false }));
		}
	};

	const handleCopyUrl = async (url) => {
		if (!url) {
			return;
		}

		try {
			await navigator.clipboard.writeText(url);
			toast.success('URL copied to clipboard', { position: toast.POSITION.BOTTOM_RIGHT });
		} catch (error) {
			toast.error('Failed to copy URL', { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	const handleOpenJobDetails = async (job) => {
		const requestId = ++jobDetailsRequest.current;
		setSelectedJob(job);
		setSelectedJobDetails(null);
		setJobDetailsError('');
		setLoading((prev) => ({ ...prev, jobDetails: true }));

		try {
			const { data } = await getRssVideoJob(job.id);
			if (requestId === jobDetailsRequest.current) {
				setSelectedJobDetails(data?.data?.job || null);
			}
		} catch (error) {
			if (requestId === jobDetailsRequest.current) {
				setJobDetailsError(getErrorMessage(error));
			}
		} finally {
			if (requestId === jobDetailsRequest.current) {
				setLoading((prev) => ({ ...prev, jobDetails: false }));
			}
		}
	};

	const handleCloseJobDetails = () => {
		jobDetailsRequest.current += 1;
		setSelectedJob(null);
		setSelectedJobDetails(null);
		setJobDetailsError('');
	};

	const handleSaveJobMetadata = async (jobId, payload) => {
		setJobMetadataSaving(true);

		try {
			const { data } = await updateRssVideoJobMetadata(jobId, payload);
			const nextJob = data?.data?.job || null;

			if (nextJob) {
				// The user may have selected another job while this save was in progress.
				setSelectedJobDetails((currentDetails) => {
					if (currentDetails?.id !== jobId) {
						return currentDetails;
					}

					return { ...currentDetails, ...nextJob };
				});

				setSelectedJob((currentJob) => {
					if (currentJob?.id !== jobId) {
						return currentJob;
					}

					return { ...currentJob, ...nextJob };
				});

				setJobs((currentJobs) =>
					currentJobs.map((job) => {
						if (job.id !== jobId) {
							return job;
						}

						return { ...job, ...nextJob };
					})
				);
			}

			toast.success(data?.data?.message || 'Job metadata updated', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		} catch (error) {
			toast.error(getErrorMessage(error), { position: toast.POSITION.BOTTOM_RIGHT });
			throw error;
		} finally {
			setJobMetadataSaving(false);
		}
	};

	const handleHardResetJob = async (jobId) => {
		if (!confirmGenerationWithoutDisplay('Resetting this job')) {
			return;
		}

		setJobHardResetting(true);

		try {
			await hardResetRssVideoJob(jobId);
			toast.success('Job hard reset requested', { position: toast.POSITION.BOTTOM_RIGHT });
			await Promise.all([loadJobs(), handleOpenJobDetails({ id: jobId })]);
		} catch (error) {
			toast.error(getErrorMessage(error), { position: toast.POSITION.BOTTOM_RIGHT });
			throw error;
		} finally {
			setJobHardResetting(false);
		}
	};

	const handleJobActionClick = (event, jobId, action) => {
		event.stopPropagation();
		handleJobAction(jobId, action);
	};

	const handleJobAction = async (jobId, action) => {
		if (action === 'retry' && !confirmGenerationWithoutDisplay('Retrying this job')) {
			return false;
		}

		const job = jobs.find((item) => item.id === jobId);
		const isCancellationUnavailable =
			loading.jobActionId === `cancel:${jobId}` ||
			isCancelBlocked(job) ||
			isTerminalStage(job?.stage);

		if (action === 'cancel' && isCancellationUnavailable) {
			return false;
		}

		const messages = {
			retry: `Retry job ${jobId}? Video generation will resume for this article.`,
			cancel: `Cancel job ${jobId}? This will request cancellation of video generation.`,
			delete: `Delete job ${jobId}? This cannot be undone.`
		};
		if (!messages[action] || !window.confirm(messages[action])) {
			return false;
		}

		setLoading((prev) => ({ ...prev, jobActionId: `${action}:${jobId}` }));

		try {
			if (action === 'cancel') {
				await cancelRssVideoJob(jobId);
				toast.success('Cancellation requested', { position: toast.POSITION.BOTTOM_RIGHT });
			}

			if (action === 'retry') {
				await retryRssVideoJob(jobId);
				toast.success('Retry requested', { position: toast.POSITION.BOTTOM_RIGHT });
			}

			if (action === 'delete') {
				await deleteRssVideoJob(jobId);
				toast.success('Job deleted', { position: toast.POSITION.BOTTOM_RIGHT });
			}

			await loadJobs();
			return true;
		} catch (error) {
			toast.error(getErrorMessage(error), { position: toast.POSITION.BOTTOM_RIGHT });
		} finally {
			setLoading((prev) => ({ ...prev, jobActionId: '' }));
		}
	};

	const handleRetryJob = async (jobId) => {
		if (await handleJobAction(jobId, 'retry')) {
			await handleOpenJobDetails({ id: jobId });
		}
	};

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
			<PageHeading
				headingName="Page Level Content"
				headingPath={['Dashboard', 'Framerate', 'Page Level Content']}
				description={FEATURE_DESCRIPTION.VIDEO_CONTENT.PAGE_LEVEL_CONTENT_VIDEOS.SUMMARY}
				showInfoButton={true}
				infoMessage={FEATURE_DESCRIPTION.VIDEO_CONTENT.PAGE_LEVEL_CONTENT_VIDEOS.INFO}
			/>

			<Box sx={{ mx: { xs: 3, md: '5rem' }, pt: 2, pb: 2 }}>
				<Box component="section" aria-label="Page Level Content Videos">
					<CheckBoxInput
						keyData={PAGE_LEVEL_CONTENT_VIDEOS_KEY}
						label="Enable Page Level Content Videos for this site"
						isObjectEditor={false}
						directValue={isSiteEnabled}
						updateDirectValue={updateSiteEnabled}
						
					/>
					{isSiteEnabled && isSectionDisabled && (
						<Alert severity="warning" sx={{ mt: 2 }}>
							Page Level Content Videos is disabled for the selected section. To display videos here, uncheck Disable for this section in Features Data &gt; Page Level Content Override.
						</Alert>
					)}
				</Box>

				<Divider sx={{ my: 4 }} />

				<Box component="section" aria-labelledby="rss-video-generation-heading">
					<Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
						<Grid item>
							<Typography
								id="rss-video-generation-heading"
								component="h2"
								variant="h4"
								fontWeight={600}
								sx={{ color: 'black' }}
							>
								RSS Video Generation
							</Typography>
						</Grid>
						<Grid item>
							<InfoButton message={featureDescription} />
						</Grid>
					</Grid>

					<Typography variant="subtitle1" color="text.secondary" sx={{ lineHeight: 1.6, mb: 3 }}>
						{FEATURE_DESCRIPTION.VIDEO_CONTENT.RSS_VIDEO_GENERATION.SUMMARY}
					</Typography>

					{!isSiteEnabled && (
						<Alert severity="warning" sx={{ mb: 2 }}>
							Enable Page Level Content Videos for this site to use RSS Video Generation.
						</Alert>
					)}
					{needsFeedSync && (
						<Alert
							severity="warning"
							sx={{ mb: 2, '& .MuiAlert-action': { alignItems: 'center', pt: 0 } }}
							action={
								<Button
									variant="contained"
									color="warning"
									size="small"
									onClick={handleSyncFeed}
									disabled={loading.saveFeed || loading.deleteFeed || loading.pollFeed}
								>
									Copy to Site Config
								</Button>
							}
						>
							The feed settings saved in this site's configuration don't match the live RSS
							service. Click "Copy to Site Config" to overwrite them with the live settings,
							then update the site to save the change.
						</Alert>
					)}

					{(
						<Grid container spacing={4}>
							<Grid item xs={12} lg={6}>
								<FeedConfigurationCard
									feedForm={feedForm}
									feed={feed}
									hasExistingFeed={hasExistingFeed}
									isFeatureEnabled={isSiteEnabled}
									isExperimentalUser={isExperimentalUser}
									loading={loading}
									feedSummary={feedSummary}
									onChange={updateFeedForm}
									onSave={handleSaveFeed}
									onPoll={handlePollFeed}
									onDelete={() => setDeleteFeedDialogOpen(true)}
								/>
							</Grid>

							<Grid item xs={12} lg={6}>
								<ManualJobCard
									jobForm={jobForm}
									loading={loading}
									onChange={updateJobForm}
									onCreate={handleCreateJob}
									isFeatureEnabled={isSiteEnabled}
									isExperimentalUser={isExperimentalUser}
								/>
							</Grid>

							<Grid item xs={12}>
								<Divider sx={{ my: 1 }} />
							</Grid>

							<Grid item xs={12}>
								<Paper variant="outlined" sx={{ p: 3 }}>
									<JobsTable
										jobs={jobs}
										loading={loading}
										onRefresh={loadJobs}
										onOpenJobDetails={handleOpenJobDetails}
										onCopyUrl={handleCopyUrl}
										isFeatureEnabled={isSiteEnabled}
										onJobAction={handleJobActionClick}
									/>
								</Paper>
							</Grid>
						</Grid>
					)}
				</Box>
			</Box>

			<JobDetailsDialog
				open={Boolean(selectedJob)}
				onClose={handleCloseJobDetails}
				job={selectedJob}
				jobDetails={selectedJobDetails}
				loading={loading.jobDetails}
				error={jobDetailsError}
				onCopyUrl={handleCopyUrl}
				onSaveMetadata={handleSaveJobMetadata}
				onHardReset={handleHardResetJob}
				onRetry={handleRetryJob}
				metadataSaving={jobMetadataSaving}
				hardResetting={jobHardResetting}
				retrying={loading.jobActionId === `retry:${selectedJob?.id}`}
				isExperimentalUser={isExperimentalUser}
				isFeatureEnabled={isSiteEnabled}
			/>

			<DeleteFeedDialog
				open={deleteFeedDialogOpen}
				siteId={siteId}
				feedUrl={feed?.config?.feedUrl}
				deleting={loading.deleteFeed}
				onClose={() => setDeleteFeedDialogOpen(false)}
				onConfirm={handleDeleteFeed}
			/>
		</Paper>
	);
};

export default PageLevelContent;
